/**
 * Eric BYOK (2026-05-12) — Guide pas-à-pas Forfait AI.
 *
 * Pour chaque service BYOK (OpenAI, AssemblyAI, OVHcloud), explique :
 *   1. À quoi sert le service dans Knoria
 *   2. Comment créer un compte
 *   3. Comment générer la clé API
 *   4. Quels frais prévoir + tarification
 *   5. Comment configurer dans Knoria
 *   6. Comment vérifier le quota / monitorer la consommation chez le fournisseur
 *
 * Page lisible, copiable, partageable au comptable / au support IT du client.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";

// Tâche 1396 — Les deux langues sur la même entrée (motif 1324b) : cette
// table vit hors composant. Les noms d'écrans des fournisseurs (Settings →
// Billing, Data controls…) restent tels quels dans les deux versions : ils
// ne sont pas traduits chez OpenAI, OVH ou AssemblyAI, et les traduire
// ferait chercher un menu qui n'existe pas.
const GUIDE = {
  openai: {
    icon: "🤖",
    title: "OpenAI — GPT-5.2 + Whisper + Vision",
    title_en: "OpenAI — GPT-5.2 + Whisper + Vision",
    role: "Cerveau IA de Knoria. Fait tout : transcription (Whisper), extraction de connaissance (GPT-5.2), reconnaissance visuelle des intervenants (Vision), chatbot RAG, AMF compliance.",
    role_en: "Knoria's AI brain. It does everything: transcription (Whisper), knowledge extraction (GPT-5.2), visual recognition of speakers (Vision), RAG chatbot, AMF compliance.",
    cost_label: "Coût mensuel typique",
    cost_label_en: "Typical monthly cost",
    cost: "20 à 150 CAD$/mois (selon volume vidéo)",
    detail_cost: "Whisper : ~0,008 CAD$/min · GPT-5.2 : ~0,01 à 0,02 CAD$/Q&R · Vision : ~0,005 CAD$/photo locuteur. Pour 10h de vidéo/mois ≈ 30-50 CAD$.",
    cost_en: "CAD$20 to 150/month (depending on video volume)",
        detail_cost_en: "Whisper: ~CAD$0.008/min · GPT-5.2: ~CAD$0.01 to 0.02 per Q&A · Vision: ~CAD$0.005 per speaker photo. For 10 h of video a month ≈ CAD$30-50.",
    steps: [
      {
        title: "Créer un compte OpenAI Platform",
        body: "Aller sur platform.openai.com et cliquer 'Sign up'. Utiliser une adresse email professionnelle (idéalement compta@votre-entreprise.com pour centraliser la facturation).",
        title_en: "Create an OpenAI Platform account",
                body_en: "Go to platform.openai.com and click 'Sign up'. Use a work email address (ideally accounting@your-company.com so the billing stays in one place).",
        link: "https://platform.openai.com/signup",
        link_label: "platform.openai.com/signup",
      },
      {
        title: "Configurer le moyen de paiement",
        body: "Settings → Billing → Add payment method. Ajouter une carte de crédit corporative. Activer le « auto-recharge » à 10$ pour ne jamais tomber à zéro pendant un traitement vidéo.",
        title_en: "Set up the payment method",
                body_en: "Settings → Billing → Add payment method. Add a corporate credit card. Turn on “auto-recharge” at $10 so you never hit zero in the middle of processing a video.",
        link: "https://platform.openai.com/settings/organization/billing/overview",
        link_label: "platform.openai.com/settings/billing",
      },
      {
        title: "Activer le bon plan (Tier 1 minimum)",
        body: "Verser un premier crédit de 10 USD$ → vous passez automatiquement Tier 1 (débloque GPT-5.2 et Whisper). Sans crédit initial, vous restez Tier Free avec quota très limité.",
        title_en: "Unlock the right plan (Tier 1 minimum)",
                body_en: "Put in a first US$10 credit → you move to Tier 1 automatically (which unlocks GPT-5.2 and Whisper). With no initial credit you stay on the Free tier, with a very tight quota.",
        link: "https://platform.openai.com/settings/organization/limits",
        link_label: "Vérifier mon tier",
        link_label_en: "Check my tier",
      },
      {
        title: "Générer la clé API",
        body: "Aller sur API Keys → 'Create new secret key'. Lui donner un nom explicite : « Knoria-Production-2026 ». Cocher TOUS les scopes (Read + Write sur models, completions, audio, embeddings). COPIER la clé immédiatement (commence par sk-proj-…) — elle ne sera plus jamais affichée.",
        title_en: "Generate the API key",
                body_en: "Go to API Keys → 'Create new secret key'. Give it a name that says what it is: “Knoria-Production-2026”. Tick ALL the scopes (Read + Write on models, completions, audio, embeddings). COPY the key straight away (it starts with sk-proj-…) — it is never shown again.",
        link: "https://platform.openai.com/api-keys",
        link_label: "platform.openai.com/api-keys",
      },
      {
        title: "Définir un budget mensuel (sécurité)",
        body: "Settings → Limits → Usage limits → mettre un Hard Limit (ex: 200 USD$/mois). Au-delà, OpenAI bloque automatiquement les requêtes. Permet d'éviter toute surprise de facture.",
        title_en: "Set a monthly budget (safety net)",
                body_en: "Settings → Limits → Usage limits → set a Hard Limit (e.g. US$200/month). Past that, OpenAI blocks requests automatically. No surprise invoice.",
        link: "https://platform.openai.com/settings/organization/limits",
        link_label: "Configurer la limite",
        link_label_en: "Set the limit",
      },
      {
        title: "Désactiver l'entraînement sur vos données (CRITIQUE — Loi 25)",
        body: "Settings → Data controls → Data Sharing → DÉCOCHER « Improve the model for everyone ». Ainsi vos vidéos / transcripts ne servent pas à entraîner les modèles OpenAI. Obligatoire pour la conformité Loi 25.",
        title_en: "Turn off training on your data (CRITICAL — Law 25)",
                body_en: "Settings → Data controls → Data Sharing → UNTICK “Improve the model for everyone”. Your videos and transcripts then do not train OpenAI's models. Required for Law 25 compliance.",
        link: "https://platform.openai.com/settings/organization/data-controls",
        link_label: "platform.openai.com/data-controls",
      },
      {
        title: "Coller la clé dans Knoria",
        body: "Retour ici → Paramètres → Forfait AI → carte OpenAI → « Configurer » → coller la clé sk-proj-… → « Tester et sauvegarder ». Knoria valide la clé contre l'API OpenAI puis la chiffre AES-256 avant stockage.",
        title_en: "Paste the key into Knoria",
                body_en: "Come back here → Settings → AI Package → OpenAI card → “Configure” → paste the sk-proj-… key → “Test and save”. Knoria checks the key against the OpenAI API, then encrypts it with AES-256 before storing it.",
        link: "/dashboard/settings/ai-package",
        link_label: "Page Forfait AI",
        link_label_en: "AI Package page",
        internal: true,
      },
      {
        title: "Suivre la consommation chez OpenAI",
        body: "Usage → graphique quotidien des tokens et $$. Vous pouvez réconcilier avec le rapport CSV/PDF généré par Knoria (Dashboard Consommation Forfait AI).",
        title_en: "Track your spending at OpenAI",
                body_en: "Usage → daily chart of tokens and dollars. You can reconcile it with the CSV/PDF report Knoria produces (AI Package consumption dashboard).",
        link: "https://platform.openai.com/usage",
        link_label: "platform.openai.com/usage",
      },
    ],
  },
  assemblyai: {
    icon: "🎙️",
    title: "AssemblyAI — Diarisation des locuteurs",
    role: "Identifie automatiquement les intervenants dans vos vidéos (« Eric a dit X à 0:42, Vincent a répondu Y à 1:15 »). Whisper transcrit le texte ; AssemblyAI dit QUI parle. Indispensable pour les vidéos avec 2+ intervenants.",
    title_en: "AssemblyAI — Speaker diarisation",
        role_en: "Works out who is speaking in your videos (“Eric said X at 0:42, Vincent answered Y at 1:15”). Whisper transcribes the words; AssemblyAI says WHO is talking. Essential as soon as a video has two or more speakers.",
    cost_label: "Coût mensuel typique",
    cost_label_en: "Typical monthly cost",
    cost: "15 à 80 CAD$/mois (selon volume vidéo)",
    detail_cost: "Tarif : 0,65 USD$/heure d'audio analysée → ~0,015 CAD$/min. 10h de vidéo/mois = ~9 CAD$. Premiers 100 dollars de crédit OFFERTS au signup.",
    cost_en: "CAD$15 to 80/month (depending on video volume)",
        detail_cost_en: "Rate: US$0.65 per hour of audio analysed → ~CAD$0.015/min. 10 h of video a month = ~CAD$9. The first $100 of credit is FREE at sign-up.",
    steps: [
      {
        title: "Créer un compte AssemblyAI",
        body: "Aller sur assemblyai.com/dashboard/signup. AssemblyAI offre 50 USD$ de crédit gratuit à l'inscription — généralement suffisant pour 2-3 mois de tests.",
        title_en: "Create an AssemblyAI account",
                body_en: "Go to assemblyai.com/dashboard/signup. AssemblyAI gives US$50 of free credit at sign-up — usually enough for two or three months of testing.",
        link: "https://www.assemblyai.com/dashboard/signup",
        link_label: "assemblyai.com/signup",
      },
      {
        title: "Confirmer l'email + se connecter au dashboard",
        body: "Lien de confirmation envoyé par AssemblyAI. Une fois connecté, vous atterrissez sur le dashboard avec votre crédit de bienvenue déjà disponible.",
        title_en: "Confirm the email and sign in to the dashboard",
                body_en: "AssemblyAI sends a confirmation link. Once signed in, you land on the dashboard with your welcome credit already there.",
        link: "https://www.assemblyai.com/app",
        link_label: "assemblyai.com/app",
      },
      {
        title: "Récupérer la clé API (déjà générée)",
        body: "Sur le dashboard, section « API Keys » (en haut à droite). AssemblyAI génère automatiquement UNE clé permanente à l'inscription. Cliquer « Reveal » puis « Copy » — c'est une chaîne de 32 caractères hexadécimaux.",
        title_en: "Pick up the API key (already generated)",
                body_en: "On the dashboard, “API Keys” section (top right). AssemblyAI generates ONE permanent key for you at sign-up. Click “Reveal” then “Copy” — it is a 32-character hexadecimal string.",
        link: "https://www.assemblyai.com/app/account",
        link_label: "Mon compte AssemblyAI",
        link_label_en: "My AssemblyAI account",
      },
      {
        title: "Ajouter un moyen de paiement (après les 50$ offerts)",
        body: "Billing → Add credit card. AssemblyAI facture à l'usage (post-payé) — pas de minimum. Vous pouvez aussi acheter des crédits prépayés.",
        title_en: "Add a payment method (once the free $50 is used up)",
                body_en: "Billing → Add credit card. AssemblyAI bills as you go — no minimum. You can also buy prepaid credit.",
        link: "https://www.assemblyai.com/app/account/billing",
        link_label: "Billing AssemblyAI",
        link_label_en: "AssemblyAI billing",
      },
      {
        title: "Coller la clé dans Knoria",
        body: "Retour ici → Paramètres → Forfait AI → carte AssemblyAI → « Configurer » → coller la clé hexadécimale → « Tester et sauvegarder ». Knoria valide la clé contre l'API AssemblyAI puis la chiffre AES-256.",
        title_en: "Paste the key into Knoria",
                body_en: "Come back here → Settings → AI Package → AssemblyAI card → “Configure” → paste the hexadecimal key → “Test and save”. Knoria checks the key against the AssemblyAI API, then encrypts it with AES-256.",
        link: "/dashboard/settings/ai-package",
        link_label: "Page Forfait AI",
        link_label_en: "AI Package page",
        internal: true,
      },
      {
        title: "Suivre la consommation chez AssemblyAI",
        body: "Dashboard → Usage. Affiche le solde restant, le coût quotidien et le nombre de fichiers traités. À comparer avec le rapport mensuel Knoria.",
        title_en: "Track your spending at AssemblyAI",
                body_en: "Dashboard → Usage. Shows the balance left, the daily cost and how many files were processed. Compare it with Knoria's monthly report.",
        link: "https://www.assemblyai.com/app/usage",
        link_label: "assemblyai.com/usage",
      },
    ],
  },
  ovh_storage: {
    icon: "🍁",
    title: "OVHcloud Object Storage (Beauharnois Québec)",
    role: "Stocke physiquement vos fichiers vidéo bruts au Canada (datacenter BHS, Beauharnois Québec). Obligatoire pour la conformité Loi 25 : les données personnelles ne quittent JAMAIS le territoire canadien.",
    title_en: "OVHcloud Object Storage (Beauharnois, Quebec)",
        role_en: "Physically stores your raw video files in Canada (BHS datacentre, Beauharnois, Quebec). Required for Law 25 compliance: personal data NEVER leaves Canadian soil.",
    cost_label: "Coût mensuel typique",
    cost_label_en: "Typical monthly cost",
    cost: "5 à 30 CAD$/mois (selon volume vidéo)",
    detail_cost: "OVH BHS Object Storage : 0,015 CAD$/GB/mois + 0,011 CAD$/GB pour l'extraction. 100 GB de vidéos = ~1,50 CAD$/mois (stockage seul). Beaucoup moins cher qu'AWS Canada Central.",
    cost_en: "CAD$5 to 30/month (depending on video volume)",
        detail_cost_en: "OVH BHS Object Storage: CAD$0.015/GB/month + CAD$0.011/GB to retrieve. 100 GB of video = ~CAD$1.50/month (storage alone). Much cheaper than AWS Canada Central.",
    steps: [
      {
        title: "Créer un compte OVHcloud Canada",
        body: "Aller sur ovhcloud.com/fr-ca → « Inscrivez-vous gratuitement ». IMPORTANT : choisir l'entité juridique CANADIENNE (sinon vous risquez de tomber sur OVH France, qui ne respecte pas Loi 25). L'URL doit contenir /fr-ca/.",
        title_en: "Create an OVHcloud Canada account",
                body_en: "Go to ovhcloud.com/en-ca → “Sign up for free”. IMPORTANT: pick the CANADIAN legal entity (otherwise you may land on OVH France, which does not satisfy Law 25). The URL must contain /en-ca/ or /fr-ca/.",
        link: "https://www.ovhcloud.com/fr-ca/auth/signup/",
        link_label: "ovhcloud.com/fr-ca/signup",
      },
      {
        title: "Activer le Public Cloud",
        body: "Une fois connecté, espace client → Public Cloud → « Créer un projet ». Choisir un nom (ex: « knoria-prod ») et ajouter un moyen de paiement. OVH facture à l'usage post-payé.",
        title_en: "Turn on Public Cloud",
                body_en: "Once signed in, control panel → Public Cloud → “Create a project”. Pick a name (e.g. “knoria-prod”) and add a payment method. OVH bills as you go.",
        link: "https://www.ovhcloud.com/fr-ca/public-cloud/",
        link_label: "OVH Public Cloud",
      },
      {
        title: "Créer un bucket en région BHS (Beauharnois)",
        body: "Dans le projet Public Cloud → Object Storage → « Créer un conteneur S3 ». TYPE : S3 (pas Swift). RÉGION : BHS - Beauharnois (Québec). Nom suggéré : knoria-{votre-org}-prod. ⚠️ Ne JAMAIS choisir GRA / SBG / RBX (France) — vous perdez la conformité Loi 25.",
        title_en: "Create a container in the BHS region (Beauharnois)",
                body_en: "In the Public Cloud project → Object Storage → “Create an S3 container”. TYPE: S3 (not Swift). REGION: BHS - Beauharnois (Quebec). Suggested name: knoria-{your-org}-prod. ⚠️ NEVER pick GRA / SBG / RBX (France) — you lose Law 25 compliance.",
        link: "https://www.ovhcloud.com/fr-ca/public-cloud/object-storage/",
        link_label: "Object Storage OVH",
        link_label_en: "OVH Object Storage",
      },
      {
        title: "Générer les clés S3 (Access Key + Secret Key)",
        body: "Onglet « Utilisateurs et rôles » dans le projet Public Cloud → « Créer un utilisateur S3 » → cocher le rôle « ObjectStore operator » → choisir le bucket créé. OVH génère immédiatement 2 valeurs : Access Key ID et Secret Access Key. COPIER LES DEUX (le secret n'est plus jamais affiché).",
        title_en: "Generate the S3 keys (Access Key + Secret Key)",
                body_en: "“Users & Roles” tab in the Public Cloud project → “Create an S3 user” → tick the “ObjectStore operator” role → pick the container you created. OVH immediately generates 2 values: Access Key ID and Secret Access Key. COPY BOTH (the secret is never shown again).",
        link: "https://help.ovhcloud.com/csm/en-public-cloud-storage-s3-getting-started",
        link_label: "Guide officiel OVH",
        link_label_en: "Official OVH guide",
      },
      {
        title: "Coller les credentials dans Knoria",
        body: "Retour ici → Paramètres → Forfait AI → carte OVHcloud → « Configurer » → Access Key ID, Secret Access Key, Nom du bucket, Région = BHS, Endpoint = https://s3.bhs.io.cloud.ovh.net → « Tester et sauvegarder ». Knoria fait un head_bucket pour vérifier les permissions.",
        title_en: "Paste the credentials into Knoria",
                body_en: "Come back here → Settings → AI Package → OVHcloud card → “Configure” → Access Key ID, Secret Access Key, container name, Region = BHS, Endpoint = https://s3.bhs.io.cloud.ovh.net → “Test and save”. Knoria runs a head_bucket to check the permissions.",
        link: "/dashboard/settings/ai-package",
        link_label: "Page Forfait AI",
        link_label_en: "AI Package page",
        internal: true,
      },
      {
        title: "Suivre la consommation OVH",
        body: "Espace client OVH → Public Cloud → Facturation. Affiche les GB stockés, les requêtes API et le coût quotidien. Réconciliable avec le rapport mensuel Knoria.",
        title_en: "Track your spending at OVH",
                body_en: "OVH control panel → Public Cloud → Billing. Shows the GB stored, the API requests and the daily cost. Can be reconciled with Knoria's monthly report.",
        link: "https://www.ovhcloud.com/manager/public-cloud/",
        link_label: "OVH Manager",
      },
    ],
  },
  // Tâche 1494 (Eric 2026-10-07) — Les vecteurs n'avaient AUCUNE entrée : le
  // lien « Voir la marche à suivre » retombait sur le guide OpenAI, qui parle
  // de transcription et de GPT, pas de vecteurs. Un guide qui répond à côté
  // est pire qu'aucun guide.
  embeddings: {
    icon: "🧭",
    title: "Vecteurs de recherche — OpenAI ou Hugging Face",
    title_en: "Search vectors — OpenAI or Hugging Face",
    role: "Rend vos connaissances trouvables. Chaque question posée au robot conversationnel est transformée en vecteur, puis comparée aux vecteurs de vos connaissances. Sans cette clé, le robot ne cherche plus que par mots-clés : il répond encore, mais il manque tout ce qui est dit autrement.",
    role_en: "Makes your knowledge findable. Every question asked to the chatbot is turned into a vector, then compared with the vectors of your knowledge. Without this key, the chatbot only searches by keyword: it still answers, but it misses everything worded differently.",
    cost_label: "Coût mensuel typique",
    cost_label_en: "Typical monthly cost",
    cost: "1 à 5 CAD$/mois",
    cost_en: "CAD$1 to 5/month",
    detail_cost: "OpenAI text-embedding-3-small : 0,02 US$ le million de jetons. Réindexer 800 connaissances coûte quelques cents, et une question posée coûte une fraction de cent. Hugging Face facture au crédit prépayé.",
    detail_cost_en: "OpenAI text-embedding-3-small: US$0.02 per million tokens. Re-indexing 800 knowledge items costs a few cents, and one question costs a fraction of a cent. Hugging Face bills from prepaid credits.",
    steps: [
      {
        title: "Choisir le fournisseur",
        body: "Le plus simple : OpenAI, parce que vous y avez déjà un compte pour la transcription et les connaissances. Hugging Face reste accepté si vous le préférez. Knoria reconnaît le fournisseur au début de la clé : sk-… pour OpenAI, hf_… pour Hugging Face. ⚠️ Une clé universelle Knoria (sk-emergent…) ne convient PAS ici : elle ferait payer les vecteurs de votre cabinet par Knoria, à l'inverse de ce que le Forfait AI promet.",
        title_en: "Pick the provider",
        body_en: "The simplest one is OpenAI, because you already have an account there for transcription and knowledge. Hugging Face is still accepted if you prefer it. Knoria recognises the provider from the start of the key: sk-… for OpenAI, hf_… for Hugging Face. ⚠️ A Knoria universal key (sk-emergent…) is NOT accepted here: it would make Knoria pay for your firm's vectors, the opposite of what the AI Package promises.",
      },
      {
        title: "Créer la clé",
        body: "OpenAI : platform.openai.com/api-keys → « Create new secret key ». Vérifiez qu'un crédit est bien chargé (Settings → Billing), sinon la clé existe mais refuse de travailler. Hugging Face : huggingface.co/settings/tokens → jeton en lecture, puis achetez du crédit prépayé (Inference Providers).",
        title_en: "Create the key",
        body_en: "OpenAI: platform.openai.com/api-keys → “Create new secret key”. Check that credit is loaded (Settings → Billing), otherwise the key exists but refuses to work. Hugging Face: huggingface.co/settings/tokens → read token, then buy prepaid credit (Inference Providers).",
        link: "https://platform.openai.com/api-keys",
        link_label: "platform.openai.com/api-keys",
      },
      {
        title: "Coller la clé dans Knoria et tester",
        body: "Paramètres → Forfait AI → carte « Vecteurs de recherche » → « Configurer » → collez la clé → « Tester ». Le test demande un VRAI vecteur, pas un simple bonjour : une clé peut être valide pour le chat et refusée pour les vecteurs (quota séparé). Le résultat annonce le fournisseur, le modèle et le nombre de dimensions.",
        title_en: "Paste the key into Knoria and test it",
        body_en: "Settings → AI Package → “Search vectors” card → “Configure” → paste the key → “Test”. The test asks for a REAL vector, not a simple hello: a key can be valid for chat and refused for vectors (separate quota). The result states the provider, the model and the number of dimensions.",
        link: "/dashboard/settings/ai-package",
        link_label: "Page Forfait AI",
        link_label_en: "AI Package page",
        internal: true,
      },
      {
        title: "Réindexer après un changement de fournisseur",
        body: "Les vecteurs d'OpenAI (1536 dimensions) et de Hugging Face (384) ne sont pas comparables. Si vous changez de fournisseur, vos anciens vecteurs sont IGNORÉS — jamais comparés de travers — jusqu'à la réindexation. Lancez-la depuis la fiche d'une vidéo : « Continuer (connaissances) » → « Réindexer la base ». Pendant ce temps, le robot cherche encore par mots-clés.",
        title_en: "Re-index after switching provider",
        body_en: "Vectors from OpenAI (1536 dimensions) and Hugging Face (384) are not comparable. If you switch provider, your old vectors are IGNORED — never compared wrongly — until re-indexing. Start it from a video page: “Continue (knowledge)” → “Re-index the database”. In the meantime the chatbot still searches by keyword.",
        link: "/dashboard/explorer",
        link_label: "Explorateur de fichiers",
        link_label_en: "File explorer",
        internal: true,
      },
    ],
  },
};

// Tâche 1396 — Lit la version anglaise si elle existe, sinon la française :
// une entrée oubliée reste lisible au lieu de disparaître de l'écran.
const L = (o, cle, lang) => (lang === "en" && o?.[`${cle}_en`] ? o[`${cle}_en`] : o?.[cle]);

function StepCard({ index, step, lang }) {
  const isInternal = !!step.internal;
  return (
    <div className="flex gap-4 p-4 rounded-lg border border-zinc-200 bg-white hover:border-blue-300 transition-colors">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7] text-white text-sm font-bold flex items-center justify-center">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-zinc-900 text-sm mb-1">{L(step, "title", lang)}</h4>
        <p className="text-sm text-zinc-700 leading-relaxed">{L(step, "body", lang)}</p>
        {step.link && (
          isInternal ? (
            <Link
              to={step.link}
              className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:underline font-medium"
            >
              → {L(step, "link_label", lang)}
            </Link>
          ) : (
            <a
              href={step.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 hover:underline font-medium"
            >
              🔗 {L(step, "link_label", lang)}
            </a>
          )
        )}
      </div>
    </div>
  );
}

function ServiceSection({ serviceKey, info, lang, isOpen, onToggle }) {
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white" data-testid={`guide-service-${serviceKey}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-5 flex items-start gap-4 hover:bg-zinc-50 transition-colors"
        data-testid={`guide-toggle-${serviceKey}`}
      >
        <div className="text-4xl flex-shrink-0">{info.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-zinc-900">{L(info, "title", lang)}</h2>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700">
              {info.steps.length} {lang === "fr" ? "étapes" : "steps"}
            </span>
          </div>
          <p className="text-sm text-zinc-600 mt-2">{L(info, "role", lang)}</p>
          <div className="mt-2 text-xs text-zinc-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>💰 <strong>{L(info, "cost_label", lang)}</strong> : {L(info, "cost", lang)}</span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">{L(info, "detail_cost", lang)}</p>
        </div>
        <div className="text-xl text-zinc-400 flex-shrink-0">{isOpen ? "−" : "+"}</div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-2 space-y-2 border-t border-zinc-100" data-testid={`guide-steps-${serviceKey}`}>
          {info.steps.map((step, i) => (
            <StepCard key={i} index={i} step={step} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AIPackageGuidePage() {
  const { lang } = useLang();
  const guide = GUIDE;
  // Eric/JS 2026-06-02 — Deep-link via ?service=ovh_storage pour ouvrir
  // automatiquement la section OVH (utilisé depuis la carte "non configuré").
  const _getDeepLinkService = () => {
    if (typeof window === "undefined") return "openai";
    const p = new URLSearchParams(window.location.search);
    const s = p.get("service");
    return (s && Object.keys(guide).includes(s)) ? s : "openai";
  };
  const [openService, setOpenService] = useState(_getDeepLinkService);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    const s = p.get("service");
    if (!s || !Object.keys(guide).includes(s)) return;
    // Laisser le panneau s'ouvrir, puis scroll doux vers la section.
    const id = `guide-service-${s}`;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  }, [guide]);

  return (
    <div className="p-4 lg:p-6 max-w-4xl" data-testid="ai-package-guide-page">
      <div className="mt-4 mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-zinc-900">
          📘 {lang === "fr" ? "Guide pas-à-pas — Forfait AI Knoria" : "Step-by-step guide — Knoria AI Package"}
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          {lang === "fr"
            ? "Marche à suivre pour créer chaque compte fournisseur, générer la clé API et la configurer dans Knoria. À partager avec votre support IT ou votre comptable."
            : "Walkthrough to create each provider account, generate the API key and configure it in Knoria. Share with your IT support or accountant."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Link
            to="/dashboard/settings/ai-package"
            className="px-3 py-1.5 text-xs rounded-lg border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 font-medium"
            data-testid="guide-go-config-btn"
          >
            ⚙️ {lang === "fr" ? "Page de configuration" : "Configuration page"}
          </Link>
          <Link
            to="/dashboard/settings/ai-package/consumption"
            className="px-3 py-1.5 text-xs rounded-lg border border-teal-300 bg-white hover:bg-teal-50 text-teal-700 font-medium"
            data-testid="guide-go-consumption-btn"
          >
            📊 {lang === "fr" ? "Voir ma consommation" : "View consumption"}
          </Link>
        </div>
      </div>

      <div className="mb-5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
        ⚠️ <strong>{lang === "fr" ? "Modèle BYOK" : "BYOK model"}</strong> :{" "}
        {lang === "fr"
          ? "Knoria n'agit pas comme revendeur. Vous créez vos comptes OpenAI / AssemblyAI / OVHcloud, vous payez DIRECTEMENT les factures de ces fournisseurs, vous gardez tout le contrôle. Knoria ne touche jamais à l'argent IA — c'est juste un logiciel SaaS."
          : "Knoria does not resell. You create your own OpenAI / AssemblyAI / OVHcloud accounts, pay these providers DIRECTLY, and keep full control. Knoria never touches AI billing — it's just SaaS software."}
      </div>

      <div className="space-y-3">
        {Object.entries(guide).map(([key, info]) => (
          <ServiceSection
            key={key}
            serviceKey={key}
            info={info}
            lang={lang}
            isOpen={openService === key}
            onToggle={() => setOpenService(openService === key ? null : key)}
          />
        ))}
      </div>
      {/* Eric 2026-06-03 — Bandeau "Besoin d'aide ? support@knoria.com" retiré
          à la demande d'Eric pour éviter le flux d'emails entrants. Le guide
          intégré suffit ; le support se fait via les canaux internes. */}
    </div>
  );
}
